/**
 * HospitalQuickAdd — add a contact or a whole location to a hospital network
 * without leaving the Sourcing Desk.
 *
 * Both writes are permanent:
 *   • contact  → POST /api/health-systems/{id}/contacts  (mirrors into CRM)
 *   • location → POST /api/admin/health-systems/{id}/facilities
 *
 * A new location changes the hospital network record itself, so that form is
 * only offered to super admins. Contacts are open to anyone working the desk.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, MapPinPlus, Loader2, X } from "lucide-react";
import { API, useAuth } from "@/App";

const DEPARTMENTS = [
  "Supply Chain", "Procurement", "Materials Management", "Accounts Payable",
  "EVS", "OR / Surgical", "SPD / Central Sterile", "Infection Prevention",
  "BioMed", "Facilities", "Pharmacy", "Lab", "Administration", "Other",
];

const FACILITY_TYPES = ["hospital", "clinic", "office", "warehouse"];

export const HospitalQuickAdd = ({ hospitalId, hospitalName, onAdded }) => {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const canAddLocation = ["super_admin", "hub_admin"].includes(user?.role);

  const [mode, setMode] = useState("");            // "" | "contact" | "location"
  const [saving, setSaving] = useState(false);
  const [contact, setContact] = useState({
    name: "", email: "", title: "", department: "Supply Chain", phone: "",
  });
  const [facility, setFacility] = useState({
    name: "", facility_type: "hospital", address: "", city: "", state: "", zip_code: "", phone: "",
  });

  if (!hospitalId) return null;

  const addContact = async () => {
    if (!contact.email.trim()) { toast.error("An email address is required"); return; }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/health-systems/${hospitalId}/contacts`,
        {
          name: contact.name.trim() || null,
          email: contact.email.trim().toLowerCase(),
          title: contact.title.trim() || null,
          department: contact.department || null,
          phone: contact.phone.trim() || null,
          is_primary: false,
        },
        { headers },
      );
      toast.success(`${r.data?.contact?.name || contact.email} added to ${hospitalName}`, {
        description: "Saved on the hospital record and mirrored into the CRM — they will be in this list from now on.",
        duration: 8000,
      });
      setContact({ name: "", email: "", title: "", department: "Supply Chain", phone: "" });
      setMode("");
      onAdded?.({ type: "contact", contact: r.data?.contact });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? (d[0]?.msg || "Could not add the contact") : (typeof d === "string" ? d : "Could not add the contact");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const addFacility = async () => {
    if (!facility.name.trim()) { toast.error("The location needs a name"); return; }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/health-systems/${hospitalId}/facilities`,
        {
          name: facility.name.trim(),
          facility_type: facility.facility_type,
          address: facility.address.trim() || null,
          city: facility.city.trim() || null,
          state: facility.state.trim().toUpperCase() || null,
          zip_code: facility.zip_code.trim() || null,
          phone: facility.phone.trim() || null,
          is_main_facility: false,
        },
        { headers },
      );
      toast.success(`${facility.name} added to the ${hospitalName} network`, {
        description: "It is on the health system permanently and will show up in the PO ship-to picker straight away.",
        duration: 9000,
      });
      setFacility({ name: "", facility_type: "hospital", address: "", city: "", state: "", zip_code: "", phone: "" });
      setMode("");
      onAdded?.({ type: "facility", facility: r.data?.facility || r.data });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? (d[0]?.msg || "Could not add the location") : (typeof d === "string" ? d : "Could not add the location");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid="hospital-quick-add">
      {!mode ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[11px] text-slate-600">
            Missing somebody at <span className="font-semibold text-slate-800">{hospitalName}</span>?
            Add them once and they stay.
          </p>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]"
              onClick={() => setMode("contact")} data-testid="quick-add-contact-btn">
              <UserPlus className="w-3 h-3 mr-1" /> Add contact
            </Button>
            {canAddLocation ? (
              <Button type="button" size="sm" variant="outline"
                className="h-7 text-[11px] border-cyan-300 text-cyan-800 hover:bg-cyan-50"
                onClick={() => setMode("location")} data-testid="quick-add-location-btn">
                <MapPinPlus className="w-3 h-3 mr-1" /> Add location
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid={`quick-add-form-${mode}`}>
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-600">
              {mode === "contact" ? `New contact · ${hospitalName}` : `New location · ${hospitalName} network`}
            </p>
            <Button type="button" size="icon" variant="ghost" className="h-6 w-6"
              onClick={() => setMode("")} data-testid="quick-add-cancel">
              <X className="w-3 h-3" />
            </Button>
          </div>

          {mode === "contact" ? (
            <>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input className="h-8 text-sm bg-white" placeholder="Full name"
                  value={contact.name} onChange={(e) => setContact(c => ({ ...c, name: e.target.value }))}
                  data-testid="quick-add-contact-name" />
                <Input className="h-8 text-sm bg-white" placeholder="email@hospital.org"
                  value={contact.email} onChange={(e) => setContact(c => ({ ...c, email: e.target.value }))}
                  data-testid="quick-add-contact-email" />
                <Input className="h-8 text-sm bg-white" placeholder="Title (Buyer, Materials Manager…)"
                  value={contact.title} onChange={(e) => setContact(c => ({ ...c, title: e.target.value }))}
                  data-testid="quick-add-contact-title" />
                <select className="h-8 text-sm rounded-md border border-slate-300 bg-white px-2"
                  value={contact.department}
                  onChange={(e) => setContact(c => ({ ...c, department: e.target.value }))}
                  data-testid="quick-add-contact-department">
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <Input className="h-8 text-sm bg-white" placeholder="Phone (optional)"
                  value={contact.phone} onChange={(e) => setContact(c => ({ ...c, phone: e.target.value }))}
                  data-testid="quick-add-contact-phone" />
              </div>
              <Button type="button" size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={addContact} disabled={saving} data-testid="quick-add-contact-save">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UserPlus className="w-3 h-3 mr-1" />}
                Save contact to {hospitalName}
              </Button>
            </>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input className="h-8 text-sm bg-white" placeholder="Facility name (e.g. Ben Taub General Hospital)"
                  value={facility.name} onChange={(e) => setFacility(f => ({ ...f, name: e.target.value }))}
                  data-testid="quick-add-facility-name" />
                <select className="h-8 text-sm rounded-md border border-slate-300 bg-white px-2"
                  value={facility.facility_type}
                  onChange={(e) => setFacility(f => ({ ...f, facility_type: e.target.value }))}
                  data-testid="quick-add-facility-type">
                  {FACILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <Input className="h-8 text-sm bg-white sm:col-span-2" placeholder="Street address"
                  value={facility.address} onChange={(e) => setFacility(f => ({ ...f, address: e.target.value }))}
                  data-testid="quick-add-facility-address" />
                <Input className="h-8 text-sm bg-white" placeholder="City"
                  value={facility.city} onChange={(e) => setFacility(f => ({ ...f, city: e.target.value }))}
                  data-testid="quick-add-facility-city" />
                <div className="grid grid-cols-2 gap-2">
                  <Input className="h-8 text-sm bg-white" placeholder="State" maxLength={2}
                    value={facility.state} onChange={(e) => setFacility(f => ({ ...f, state: e.target.value }))}
                    data-testid="quick-add-facility-state" />
                  <Input className="h-8 text-sm bg-white" placeholder="ZIP"
                    value={facility.zip_code} onChange={(e) => setFacility(f => ({ ...f, zip_code: e.target.value }))}
                    data-testid="quick-add-facility-zip" />
                </div>
              </div>
              <p className="text-[10px] text-slate-500">
                The street address matters — it is what the PO ship-to matcher reads when it decides
                whether a purchase order is going to this dock.
              </p>
              <Button type="button" size="sm" className="h-8 bg-cyan-600 hover:bg-cyan-700 text-white"
                onClick={addFacility} disabled={saving} data-testid="quick-add-facility-save">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MapPinPlus className="w-3 h-3 mr-1" />}
                Add location to the network
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HospitalQuickAdd;
